// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
/* eslint-disable @typescript-eslint/no-explicit-any */
import { PrimaryTemplate } from 'components/templates/PrimaryTemplate';
import { useTranslation } from 'react-i18next';
import styles from './ImportCSV.module.scss';
import { createAxios } from 'ts/createAxios';
import { useRef, useState } from 'react';
import { getBase64 } from 'components/molecules/UploadFiles';
import { IconDelete } from 'components/atoms/icons/IconDelete';
import { msg } from 'util/message';
import { formatBytes } from 'ts/formatBytes';
import { IconImport } from 'components/atoms/icons/IconImport';
import { toast } from 'react-toastify';
import { validateFiles } from 'util/validator';
import { IconChevronDown } from 'components/atoms/icons/IconChevronDown';
import { IconChevronUp } from 'components/atoms/icons/IconChevronUp';

const ImportCSV = () => {
  const api = createAxios();
  const inputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const [fileObject, setFileObject] = useState<{
    file: File | undefined;
    base64: string;
  }>({
    file: undefined,
    base64: '',
  });
  const [error, setError] = useState<{
    type: 'validation' | 'server';
    content: string;
  }>();
  const [isErrorCollapsed, setIsErrorCollapsed] = useState(false);

  // Parse error content to detect if it's structured error format
  const parseErrorContent = (content: string) => {
    const lines = content.split('\n').filter((line) => line.trim());
    const errors: Array<{ cells: string[]; message: string }> = [];

    // Check if content follows the pattern: "CellRef Message"
    const isStructuredError = lines.some((line) =>
      /^[A-Z]+\d+(\s*,\s*[A-Z]+\d+)*\s+.+/.test(line.trim())
    );

    if (!isStructuredError) {
      return { isStructured: false, errors: [], originalContent: content };
    }

    lines.forEach((line) => {
      const match = line.match(/^([A-Z]+\d+(?:\s*,\s*[A-Z]+\d+)*)\s+(.+)$/);
      if (match) {
        const cellsStr = match[1];
        const message = match[2];
        const cells = cellsStr.split(',').map((cell) => cell.trim());
        errors.push({ cells, message });
      }
    });

    return { isStructured: true, errors, originalContent: content };
  };

  const renderErrorContent = (content: string) => {
    const parsed = parseErrorContent(content);

    if (!parsed.isStructured) {
      return <p>{content}</p>;
    }

    return (
      <>
        <p>{t('upload_csv.structural_error')}</p>

        <div className={styles.structuredError}>
          <div
            className={styles.errorHeader}
            onClick={() => setIsErrorCollapsed(!isErrorCollapsed)}
          >
            <span className={styles.errorTitle}>
              {t('upload_csv.total_error', { count: parsed.errors.length })}
            </span>
            <button className={styles.collapseButton}>
              {isErrorCollapsed ? <IconChevronDown /> : <IconChevronUp />}
            </button>
          </div>

          {!isErrorCollapsed && (
            <div className={styles.tableContainer}>
              <table className={styles.errorTable}>
                <thead>
                  <tr>
                    <th>{t('upload_csv.cell')}</th>
                    <th>{t('upload_csv.error')}</th>
                  </tr>
                </thead>
              </table>
              <div className={styles.tableBody}>
                <table className={styles.errorTable}>
                  <tbody>
                    {parsed.errors.map((errorItem, index) => (
                      <tr key={index}>
                        <td className={styles.cellColumn}>
                          {errorItem.cells.join(', ')}
                        </td>
                        <td className={styles.messageColumn}>
                          {errorItem.message}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </>
    );
  };

  const handleChangeFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(undefined);

    if (!e.target.files?.length) {
      setFileObject({ file: undefined, base64: '' });
      return;
    }

    const msgError = validateFiles({
      files: e.target.files,
    });

    if (msgError) {
      setError({
        type: 'validation',
        content: msgError,
      });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getBase64(e.target.files[0], (res: any) => {
      setFileObject({ file: e.target.files?.[0], base64: res });
      e.target.value = '';
    });
  };

  const handleSubmitImportFile = async () => {
    setError(undefined);

    try {
      const currentTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      await api.post(`/emissions/import?timezone=${currentTz}`, {
        file: fileObject.base64,
      });
      toast.success(t('messages.M_026'));

      if (inputRef && inputRef.current?.value) {
        inputRef.current.value = '';
      }
      setFileObject({ file: undefined, base64: '' });
    } catch (e: any) {
      setIsErrorCollapsed(true);
      setError({
        type: 'server',
        content:
          e.response?.data?.message || t('messages.M_038') || msg('M_038'),
      });
    }
  };
  return (
    <PrimaryTemplate h1={t('nav.importCSV')}>
      <div className={styles.importCSV}>
        <div className={styles.modalContent}>
          <div className={styles.description}>
            <div className={styles.uploadFileContent}>
              <div className={styles.uploadFileIcon}>
                <IconImport />
              </div>
              <span>{t('upload_csv.select_to_import')}</span>
              <p>{t('upload_csv.file_size')}</p>
              <label htmlFor="file-upload" className={styles.customFileUpload}>
                <p className={styles.uploadFileButton}>
                  {t('upload_csv.select_file')}
                </p>
              </label>
              <input
                ref={inputRef}
                id="file-upload"
                name="file"
                type="file"
                accept=".csv"
                onChange={handleChangeFile}
              />
            </div>
          </div>

          {fileObject.file && (
            <div className={styles.uploadedFileInfoWrapper}>
              <div className={styles.uploadFileInfo}>
                <div className={styles.uploadFileName}>
                  <span>{fileObject.file?.name}</span>
                </div>

                <div className={styles.uploadFileAction}>
                  <div className={styles.uploadFileSize}>
                    {formatBytes(fileObject.file?.size)}
                  </div>

                  <div
                    className={styles.deleteIcon}
                    onClick={() => {
                      if (inputRef && inputRef.current?.value) {
                        inputRef.current.value = '';
                      }
                      setFileObject({ file: undefined, base64: '' });
                      setError(undefined);
                    }}
                  >
                    <IconDelete />
                  </div>
                </div>
              </div>

              {error && (
                <div className={styles.error}>
                  {renderErrorContent(error.content)}
                </div>
              )}
            </div>
          )}

          <div className={styles.uploadFileButtonSubmit}>
            <button
              disabled={!fileObject.file || error?.type === 'validation'}
              className={styles.submitButton}
              type="button"
              onClick={handleSubmitImportFile}
            >
              {t('upload_csv.button_upload')}
            </button>
          </div>
        </div>
      </div>
    </PrimaryTemplate>
  );
};

export default ImportCSV;
